/**
 * 저장 전(pending) Transfer-in 행이 양도인 잔여 주식에 미치는 영향
 */

import {
    isMemberShareTransferIn,
    isMemberShareTransferOut,
} from './myRegisterEntityMemberShareInfo';
import {
    getTransferDetailTforCertNo,
    getTransferDetailTforShares,
    resolveTransferDetailsFromRecord,
    type TransferDetailRow,
} from './myRegisterTransferDetails';

function normalizeCertNoForMatch(cert: unknown): string {
    const digits = String(cert ?? '').trim().replace(/\D/g, '');
    if (!digits) return '';
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? String(n) : String(cert ?? '').trim();
}

/** Cert No 기준 — 동일 인증서는 contact 중복과 무관하게 한 번만 차감 */
export function buildTransferorUsageKey(contactCd: string, certNo: string): string {
    const cert = normalizeCertNoForMatch(certNo);
    if (cert) return cert;
    const cd = String(contactCd || '').trim();
    return cd ? `${cd}|` : '';
}

export function buildTransferorUsageKeyFromDetail(detail: TransferDetailRow): string {
    const cd = String(detail.TFOR_CONTACT_CD ?? '').trim();
    const cert = getTransferDetailTforCertNo(detail);
    return buildTransferorUsageKey(cd, cert);
}

function parseDetailShares(detail: TransferDetailRow): number {
    const n = Number(String(getTransferDetailTforShares(detail)).replace(/,/g, ''));
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function resolveShareInfoForPendingRow(share: Record<string, unknown>): Record<string, unknown> {
    if (share.share_info && typeof share.share_info === 'object') {
        return share.share_info as Record<string, unknown>;
    }
    const raw = share.shareholderJsonData ?? share.SHAREHOLDER_JSON_DATA;
    if (raw && typeof raw === 'object') {
        const root = raw as Record<string, unknown>;
        if (root.share_info && typeof root.share_info === 'object') {
            return root.share_info as Record<string, unknown>;
        }
    }
    if (typeof raw === 'string' && raw.trim()) {
        try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            if (parsed.share_info && typeof parsed.share_info === 'object') {
                return parsed.share_info as Record<string, unknown>;
            }
        } catch {
            // ignore invalid json
        }
    }
    return {};
}

/** pending/저장 전 Transfer-in(양수) 행 여부 */
export function isPendingTransferInShareRow(share: Record<string, unknown>): boolean {
    const info = resolveShareInfoForPendingRow(share);
    if (!isMemberShareTransferIn(share, info)) return false;
    if (isMemberShareTransferOut(share, info)) return false;
    const eventType = String(info.event_type ?? info.eventType ?? '').trim().toLowerCase();
    return eventType === 'transfer';
}

/**
 * 다른 pending Transfer-in 행에서 이미 배정된 양도인·인증서별 주식 수
 * @param excludeShare 편집 중인 행(자기 배정분은 차감 대상에서 제외)
 */
export function collectPendingTransferorUsage(
    sharesList: Record<string, unknown>[],
    options?: { excludeShare?: Record<string, unknown> | null },
): Map<string, number> {
    const usage = new Map<string, number>();
    const exclude = options?.excludeShare;

    for (const share of sharesList) {
        if (exclude && share === exclude) continue;
        if (!isPendingTransferInShareRow(share)) continue;

        const details = resolveTransferDetailsFromRecord(share);
        for (const detail of details) {
            const key = buildTransferorUsageKeyFromDetail(detail);
            if (!key || key === '|') continue;
            const units = parseDetailShares(detail);
            if (units <= 0) continue;
            usage.set(key, (usage.get(key) ?? 0) + units);
        }
    }

    return usage;
}

export function getAdjustedTransferorAvailable(
    baseAvailable: number,
    usageKey: string,
    pendingUsage: Map<string, number>,
): number {
    const used = pendingUsage.get(usageKey) ?? 0;
    return Math.max(0, baseAvailable - used);
}

/** pending sharesList 에서 사용 중인 최대 Cert No (다음 Transfer cert 채번용) */
export function resolveMaxCertNoFromSharesList(sharesList: Record<string, unknown>[]): number {
    let max = 0;
    for (const share of sharesList) {
        const cert = String(
            share.AC_CERTIFICATE_NO
            ?? share.ACQ_CERTI_NO
            ?? share.CERT_NO
            ?? share.CERTI_NO
            ?? '',
        ).trim();
        const n = parseInt(cert.replace(/\D/g, ''), 10);
        if (Number.isFinite(n) && n > max) max = n;
    }
    return max;
}
